// Remove-then-inject strategy for per-route <head> tags.
//
// Rationale: the prerender step writes managed tags into build/<route>/index.html
// with a data-seo-managed marker. On client hydration React does NOT touch those
// tags. To avoid duplicates when the client navigates client-side (SPA route
// change), we clear all managed tags and re-inject fresh ones with the same
// marker. This is idempotent and duplicate-proof.
//
// "Managed" selectors (see spec):
//   link[rel=canonical]
//   link[rel=alternate][hreflang]
//   meta[name=description]
//   meta[property^=og:] EXCEPT og:site_name
//   meta[name^=twitter:]
//   script[type=application/ld+json][data-seo-managed]

const MANAGED_ATTR = "data-seo-managed";

function isManagedMeta(el) {
  const prop = el.getAttribute("property") || "";
  const name = el.getAttribute("name") || "";
  if (name === "description") return true;
  if (prop.startsWith("og:") && prop !== "og:site_name") return true;
  if (name.startsWith("twitter:")) return true;
  return false;
}

function clearManagedTags() {
  const head = document.head;
  // Canonical + hreflang alternates
  head.querySelectorAll('link[rel="canonical"]').forEach((n) => n.remove());
  head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((n) => n.remove());
  // description, og:*, twitter:*
  head.querySelectorAll("meta").forEach((n) => {
    if (isManagedMeta(n)) n.remove();
  });
  // Managed JSON-LD blocks
  head.querySelectorAll(`script[type="application/ld+json"][${MANAGED_ATTR}]`).forEach((n) => n.remove());
}

function meta(attrKey, attrVal, content) {
  const el = document.createElement("meta");
  el.setAttribute(attrKey, attrVal);
  el.setAttribute("content", content);
  el.setAttribute(MANAGED_ATTR, "");
  return el;
}

function linkTag(attrs) {
  const el = document.createElement("link");
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  el.setAttribute(MANAGED_ATTR, "");
  return el;
}

/**
 * Inject the SEO descriptor produced by computeSeo() into document.head.
 * Also updates document.title and <html lang>.
 */
export function injectSeoTags(seo) {
  if (typeof document === "undefined") return;
  clearManagedTags();

  document.documentElement.lang = seo.htmlLang;
  if (seo.title) document.title = seo.title;

  const frag = document.createDocumentFragment();

  frag.appendChild(linkTag({ rel: "canonical", href: seo.canonical }));
  for (const a of seo.alternates) {
    frag.appendChild(linkTag({ rel: "alternate", hreflang: a.hreflang, href: a.href }));
  }

  frag.appendChild(meta("name", "description", seo.description));
  frag.appendChild(meta("name", "robots", seo.robots));

  frag.appendChild(meta("property", "og:type", seo.ogType));
  frag.appendChild(meta("property", "og:url", seo.ogUrl));
  frag.appendChild(meta("property", "og:title", seo.ogTitle));
  frag.appendChild(meta("property", "og:description", seo.ogDescription));
  frag.appendChild(meta("property", "og:image", seo.ogImage));
  frag.appendChild(meta("property", "og:image:width", String(seo.ogImageWidth)));
  frag.appendChild(meta("property", "og:image:height", String(seo.ogImageHeight)));
  frag.appendChild(meta("property", "og:image:alt", seo.ogImageAlt));
  frag.appendChild(meta("property", "og:locale", seo.ogLocale));
  if (seo.ogLocaleAlternate) {
    frag.appendChild(meta("property", "og:locale:alternate", seo.ogLocaleAlternate));
  }

  frag.appendChild(meta("name", "twitter:card", seo.twitterCard));
  frag.appendChild(meta("name", "twitter:title", seo.twitterTitle));
  frag.appendChild(meta("name", "twitter:description", seo.twitterDescription));
  frag.appendChild(meta("name", "twitter:image", seo.twitterImage));
  frag.appendChild(meta("name", "twitter:image:alt", seo.twitterImageAlt));

  if (seo.jsonLd) {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute(MANAGED_ATTR, "");
    s.text = JSON.stringify(seo.jsonLd);
    frag.appendChild(s);
  }

  // meta[name=robots] duplicates the note above; keep injection idempotent by
  // removing any pre-existing robots meta before we insert ours. (robots is
  // not in the "managed" cleanup pass because we want the static default to
  // keep working until the client can override it.)
  document.head.querySelectorAll('meta[name="robots"]').forEach((n) => n.remove());

  document.head.appendChild(frag);
}
